import { useEffect, useState } from "react";

interface Props {
  title?: string;
  subtitle?: string;
  onSubmit: (passcode: string) => void;
  onCancel: () => void;
  errorRemaining?: number;
  errorMessage?: string;
  cooldownEndsAt?: number;
}

export function PasscodeSheet({
  title = "Unlock sensitive vaults",
  subtitle,
  onSubmit,
  onCancel,
  errorRemaining,
  errorMessage,
  cooldownEndsAt,
}: Props) {
  const [digits, setDigits] = useState<string>("");
  const [shake, setShake] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);

  useEffect(() => {
    if (errorRemaining !== undefined) {
      setShake(true);
      setDigits("");
      const t = setTimeout(() => setShake(false), 400);
      return () => clearTimeout(t);
    }
  }, [errorRemaining]);

  useEffect(() => {
    if (errorMessage) {
      setShake(true);
      setDigits("");
      const t = setTimeout(() => setShake(false), 400);
      return () => clearTimeout(t);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (!cooldownEndsAt) {
      setCooldownRemaining(0);
      return;
    }
    const tick = () => setCooldownRemaining(Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000)));
    tick();
    const i = setInterval(tick, 500);
    return () => clearInterval(i);
  }, [cooldownEndsAt]);

  const press = (d: string) => {
    if (cooldownRemaining > 0) return;
    if (digits.length >= 6) return;
    const next = digits + d;
    setDigits(next);
    if (next.length === 6) {
      onSubmit(next);
      setDigits("");
    }
  };
  const backspace = () => setDigits(d => d.slice(0, -1));

  const mm = Math.floor(cooldownRemaining / 60);
  const ss = String(cooldownRemaining % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end z-50" role="dialog" aria-modal="true">
      <div className="w-full bg-attic-surface rounded-t-2xl p-4">
        <div className="w-8 h-1 bg-attic-border rounded mx-auto mb-3" />
        <h3 className="text-center font-semibold mb-1">{title}</h3>
        {subtitle && <p className="text-center text-attic-dim text-xs mb-4">{subtitle}</p>}
        {cooldownRemaining > 0 && (
          <p className="text-center text-red-600 text-xs mb-3">Try again in {mm}:{ss}</p>
        )}
        {errorRemaining !== undefined && cooldownRemaining === 0 && digits.length === 0 && (
          <p className="text-center text-red-600 text-xs font-semibold mb-3">
            Incorrect · {errorRemaining} tries remaining
          </p>
        )}
        {errorMessage && cooldownRemaining === 0 && errorRemaining === undefined && digits.length === 0 && (
          <p className="text-center text-red-600 text-xs font-semibold mb-3">{errorMessage}</p>
        )}
        <div className={`flex justify-center gap-3 mb-6 ${shake ? "animate-pulse" : ""}`}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className={`w-3.5 h-3.5 rounded-full border-2 ${
                errorRemaining !== undefined && digits.length === 0
                  ? "border-red-600 bg-red-600"
                  : "border-attic-accent " + (i < digits.length ? "bg-attic-accent" : "")
              }`}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
          {["1","2","3","4","5","6","7","8","9"].map(d => (
            <button
              key={d}
              onClick={() => press(d)}
              className="bg-attic-surface border border-attic-border rounded-lg py-3 text-xl"
            >
              {d}
            </button>
          ))}
          <button onClick={onCancel} className="text-attic-accent text-sm">Cancel</button>
          <button onClick={() => press("0")} className="bg-attic-surface border border-attic-border rounded-lg py-3 text-xl">0</button>
          <button onClick={backspace} className="text-attic-accent text-sm">⌫</button>
        </div>
      </div>
    </div>
  );
}
